/**
 * 📸 CHECK-IN/CHECK-OUT SECTION COMPONENT
 * Displays detailed check-in and check-out information for disputes
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ImageList,
  ImageListItem,
  Tabs,
  Tab,
  Divider,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Image as ImageIcon,
  ZoomIn,
  Download,
  CompareArrows,
  Person,
  HomeWork,
  Info,
  Close,
  Speed,
  LocalGasStation,
  ReportProblem,
  Notes,
  AttachMoney,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

interface CheckinCheckoutData {
  booking: {
    id: string;
    bookingNumber: string;
    renterName: string;
    hostName: string;
    vehicleName: string;
  };
  checkin: {
    completedAt: string | null;
    renter: {
      confirmedAt: string | null;
      photos: string[];
      mileage: number | null;
      fuelLevel: number | null;
      damageReport: string | null;
      notes: string | null;
    };
    host: {
      confirmedAt: string | null;
      photos: string[];
      damageReport: string | null;
      notes: string | null;
    };
    hasDispute: boolean;
  };
  checkout: {
    completedAt: string | null;
    renter: {
      confirmedAt: string | null;
      photos: string[];
      mileage: number | null;
      fuelLevel: number | null;
      damageReport: string | null;
      notes: string | null;
    };
    host: {
      confirmedAt: string | null;
      photos: string[];
      damageReport: string | null;
      notes: string | null;
    };
    hasDispute: boolean;
  };
  dispute: {
    type: string;
    raisedBy: string;
    reason: string;
    raisedAt: string;
    status: string;
  } | null;
}

interface CheckinCheckoutSectionProps {
  data: CheckinCheckoutData;
  onResolveDispute?: (resolution: {
    decision: 'side_with_host' | 'side_with_renter' | 'split_responsibility';
    resolution: string;
    chargeAmount?: number;
    refundAmount?: number;
  }) => void;
}

export const CheckinCheckoutSection: React.FC<CheckinCheckoutSectionProps> = ({
  data,
  onResolveDispute,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState<string[]>([]);
  const [galleryTitle, setGalleryTitle] = useState('');
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [compareMode, setCompareMode] = useState(false);

  // Resolution state
  const [resolutionDialogOpen, setResolutionDialogOpen] = useState(false);
  const [decision, setDecision] = useState<'side_with_host' | 'side_with_renter' | 'split_responsibility'>('side_with_host');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [chargeAmount, setChargeAmount] = useState('');
  const [refundAmount, setRefundAmount] = useState('');

  const openPhotoGallery = (photos: string[], title: string) => {
    setGalleryPhotos(photos);
    setGalleryTitle(title);
    setSelectedPhotoIndex(0);
    setPhotoGalleryOpen(true);
  };

  const handleComparePhotos = () => {
    // Combine check-in and check-out photos for comparison
    const checkinPhotos = [
      ...data.checkin.renter.photos,
      ...data.checkin.host.photos,
    ];
    const checkoutPhotos = [
      ...data.checkout.renter.photos,
      ...data.checkout.host.photos,
    ];

    setGalleryPhotos([...checkinPhotos, ...checkoutPhotos]);
    setGalleryTitle('Check-in vs Check-out Comparison');
    setCompareMode(true);
    setPhotoGalleryOpen(true);
  };

  const handleDownloadPhotos = async (photos: string[]) => {
    toast('Downloading photos... (Feature requires ZIP library integration)');
    // In production, this would download all photos as a ZIP file
  };

  const handleResolveDispute = () => {
    if (!resolutionNotes.trim()) {
      toast.error('Please provide resolution notes');
      return;
    }

    const resolution = {
      decision,
      resolution: resolutionNotes,
      chargeAmount: chargeAmount ? parseFloat(chargeAmount) : undefined,
      refundAmount: refundAmount ? parseFloat(refundAmount) : undefined,
    };

    onResolveDispute?.(resolution);
    setResolutionDialogOpen(false);
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'Not completed';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderPartySection = (
    title: string,
    icon: React.ReactNode,
    partyData: any,
    type: 'checkin' | 'checkout'
  ) => {
    const mileageDiff = type === 'checkout' && data.checkin.renter.mileage && partyData.mileage
      ? partyData.mileage - data.checkin.renter.mileage
      : null;

    return (
      <Paper elevation={0} sx={{ p: 2, backgroundColor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {icon}
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {partyData.confirmedAt && (
            <Chip
              icon={<CheckCircle />}
              label="Confirmed"
              size="small"
              color="success"
              variant="outlined"
              sx={{ ml: 'auto' }}
            />
          )}
        </Box>

        <Grid container spacing={2}>
          {partyData.confirmedAt && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Confirmed at:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatDateTime(partyData.confirmedAt)}
              </Typography>
            </Grid>
          )}

          {partyData.photos && partyData.photos.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Photos:
                </Typography>
                <Button
                  size="small"
                  startIcon={<ImageIcon />}
                  onClick={() => openPhotoGallery(partyData.photos, `${title} Photos`)}
                >
                  View {partyData.photos.length} Photo{partyData.photos.length !== 1 ? 's' : ''}
                </Button>
              </Box>
            </Grid>
          )}

          {partyData.mileage !== null && partyData.mileage !== undefined && (
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Speed sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Mileage:
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {partyData.mileage.toLocaleString()} km
                {mileageDiff && (
                  <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
                    (+{mileageDiff} km driven)
                  </Typography>
                )}
              </Typography>
            </Grid>
          )}

          {partyData.fuelLevel !== null && partyData.fuelLevel !== undefined && (
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <LocalGasStation sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Fuel Level:
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {partyData.fuelLevel}%
              </Typography>
            </Grid>
          )}

          {partyData.damageReport && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <ReportProblem sx={{ fontSize: 16, color: 'warning.main' }} />
                <Typography variant="caption" color="text.secondary">
                  Damage Report:
                </Typography>
              </Box>
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2">{partyData.damageReport}</Typography>
              </Alert>
            </Grid>
          )}

          {partyData.notes && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Notes sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Notes:
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                "{partyData.notes}"
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  const renderCheckInOut = (type: 'checkin' | 'checkout') => {
    const sectionData = type === 'checkin' ? data.checkin : data.checkout;
    const hasDispute = sectionData.hasDispute;

    return (
      <Box>
        {/* Section Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              {type === 'checkin' ? 'Check-In' : 'Check-Out'}
              {sectionData.completedAt && <Chip label="Completed" size="small" color="success" />}
              {hasDispute && <Chip label="DISPUTE" size="small" color="error" />}
            </Typography>
            {sectionData.completedAt && (
              <Typography variant="caption" color="text.secondary">
                Completed: {formatDateTime(sectionData.completedAt)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Download />}
              onClick={() => handleDownloadPhotos([...sectionData.renter.photos, ...sectionData.host.photos])}
            >
              Download All
            </Button>
            {type === 'checkout' && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<CompareArrows />}
                onClick={handleComparePhotos}
              >
                Compare with Check-in
              </Button>
            )}
          </Box>
        </Box>

        {/* Renter and Host Sections */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {renderPartySection('Renter (' + data.booking.renterName + ')', <Person color="primary" />, sectionData.renter, type)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderPartySection('Host (' + data.booking.hostName + ')', <HomeWork color="primary" />, sectionData.host, type)}
          </Grid>
        </Grid>

        {/* Completion Status */}
        {sectionData.completedAt ? (
          <Alert severity="success" sx={{ mt: 3 }} icon={<CheckCircle />}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {type === 'checkin' ? 'Customer checked in: the trip started' : 'Customer checked out: the trip ended'}
            </Typography>
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mt: 3 }} icon={<Info />}>
            <Typography variant="body2">
              {type === 'checkin'
                ? 'Not checked in. Check-in is only required for self-drive; other bookings start by their start time'
                : 'Not checked out. The booking completes at its end time'}
            </Typography>
          </Alert>
        )}

        {/* Dispute Section */}
        {hasDispute && data.dispute && type === 'checkout' && (
          <Paper elevation={2} sx={{ mt: 3, p: 3, border: '2px solid', borderColor: 'error.main' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="error" />
              Dispute Resolution Required
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Raised By:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.dispute.raisedBy}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Raised At:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDateTime(data.dispute.raisedAt)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Reason:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.dispute.reason}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Status:</Typography>
                <Chip
                  label={data.dispute.status.toUpperCase()}
                  size="small"
                  color={data.dispute.status === 'resolved' ? 'success' : 'error'}
                  sx={{ mt: 0.5 }}
                />
              </Grid>
            </Grid>

            {data.dispute.status !== 'resolved' && onResolveDispute && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => setResolutionDialogOpen(true)}
              >
                Resolve Dispute
              </Button>
            )}
          </Paper>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Check-In" />
        <Tab label="Check-Out" />
      </Tabs>

      {selectedTab === 0 && renderCheckInOut('checkin')}
      {selectedTab === 1 && renderCheckInOut('checkout')}

      {/* Photo Gallery Dialog */}
      <Dialog
        open={photoGalleryOpen}
        onClose={() => {
          setPhotoGalleryOpen(false);
          setCompareMode(false);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{galleryTitle}</Typography>
          <IconButton onClick={() => setPhotoGalleryOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {galleryPhotos.length > 0 ? (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <img
                  src={galleryPhotos[selectedPhotoIndex]}
                  alt={`Photo ${selectedPhotoIndex + 1}`}
                  style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                <Button
                  disabled={selectedPhotoIndex === 0}
                  onClick={() => setSelectedPhotoIndex(selectedPhotoIndex - 1)}
                >
                  Previous
                </Button>
                <Typography variant="body2">
                  {selectedPhotoIndex + 1} / {galleryPhotos.length}
                </Typography>
                <Button
                  disabled={selectedPhotoIndex === galleryPhotos.length - 1}
                  onClick={() => setSelectedPhotoIndex(selectedPhotoIndex + 1)}
                >
                  Next
                </Button>
              </Box>
            </Box>
          ) : (
            <Alert severity="info">No photos available</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Download />} onClick={() => window.open(galleryPhotos[selectedPhotoIndex], '_blank')}>
            Download
          </Button>
          <Button onClick={() => setPhotoGalleryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Resolution Dialog */}
      <Dialog open={resolutionDialogOpen} onClose={() => setResolutionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Dispute</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Admin Decision:
              </Typography>
              <RadioGroup value={decision} onChange={(e) => setDecision(e.target.value as any)}>
                <FormControlLabel
                  value="side_with_renter"
                  control={<Radio />}
                  label="Side with Renter (No damage / Pre-existing damage)"
                />
                <FormControlLabel
                  value="side_with_host"
                  control={<Radio />}
                  label="Side with Host (Charge renter for damage)"
                />
                <FormControlLabel
                  value="split_responsibility"
                  control={<Radio />}
                  label="Split Responsibility"
                />
              </RadioGroup>
            </FormControl>

            {decision === 'side_with_host' || decision === 'split_responsibility' ? (
              <TextField
                fullWidth
                label="Charge Amount (₦)"
                type="number"
                value={chargeAmount}
                onChange={(e) => setChargeAmount(e.target.value)}
                InputProps={{
                  startAdornment: <AttachMoney />
                }}
              />
            ) : null}

            {decision === 'side_with_renter' || decision === 'split_responsibility' ? (
              <TextField
                fullWidth
                label="Refund Amount (₦)"
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                InputProps={{
                  startAdornment: <AttachMoney />
                }}
              />
            ) : null}

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Resolution Notes *"
              placeholder="Explain your decision and reasoning..."
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolutionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResolveDispute} variant="contained" disabled={!resolutionNotes.trim()}>
            Resolve Dispute
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
